import { ReactNode } from 'react';

interface FieldProps {
  /** Label text shown in the notched border. */
  label: ReactNode;
  /** Renders a small red asterisk after the label. */
  required?: boolean;
  /** Optional inline hint shown next to the label. */
  hint?: ReactNode;
  /** Class on the outer fieldset (mostly for tests). */
  className?: string;
  children: ReactNode;
}

/**
 * Notched-outline label primitive built on native `<fieldset>` + `<legend>`.
 *
 * Produces the same DOM shape MUI's outlined TextField emits, but without
 * adding MUI surface area inside the `.ali-edit-object` scope (where the
 * universal selector + custom focus color would visually drift).
 *
 * Usage:
 *   <Field label="Title of work" required>
 *     <Input value={...} onChange={...} />
 *   </Field>
 *
 * Inside `.field`, nested `.input` / `.select` / `.textarea` lose their
 * own border so only the fieldset border draws.
 */
export const Field = ({
  label,
  required,
  hint,
  className,
  children,
}: FieldProps) => (
  <fieldset className={className ? `field ${className}` : 'field'}>
    <legend>
      {label}
      {required && <span className="req">*</span>}
      {hint && <span className="hint"> {hint}</span>}
    </legend>
    {children}
  </fieldset>
);
